import React, { useContext, useMemo } from 'react';
import clsx from 'clsx';

import { AppContext } from '../../AppContext';
import { Badge } from '../catalyst/badge';
import CopyIcon from '../../assets/copy-simple.svg';
import CheckIcon from '../../assets/check.svg';
import SearchResultPartsBloomfireBusinessLogicCard from './SearchResultPartsBloomfireBusinessLogicCard';
import FeedbackPanel from './FeedbackPanel';

const SOURCE_BADGE_META = {
	rc: { label: 'Past Cases', color: 'blue' },
	snl: { label: 'Serial Lookup', color: 'violet' },
	parsed: { label: 'Manual', color: 'amber' },
	web: { label: 'Website', color: 'sky' },
};

const FALLBACK_IMAGE =
	'https://www.partstown.com/images/partstownContentCatalog/source/no-results-banner_285x314.jpg';

function normalizeSources(part) {
	if (!part) {
		return [];
	}

	const rawSources = part.data_sources ?? part.data_source ?? [];

	if (Array.isArray(rawSources)) {
		return rawSources.map((source) => (source || '').toString().toLowerCase());
	}

	if (typeof rawSources === 'string') {
		return [rawSources.toLowerCase()];
	}

	return [];
}

function partHasRenderableContent(part) {
	if (!part || typeof part !== 'object') {
		return false;
	}

	const { mfgstockcode, stockcode, optimizeddescription, description, imageurl } = part;

	if (
		!((mfgstockcode && mfgstockcode.trim() !== '') ||
		(stockcode && stockcode.trim() !== '') ||
		(optimizeddescription && optimizeddescription.trim() !== '') ||
		(description && description.trim() !== ''))
	) {
		return false;
	}

	const sources = normalizeSources(part);

	return sources.length > 0 || Boolean(imageurl);
}

const BLOOMFIRE_SENTINELS = new Set(['bloomfire', 'business_logic']);

function normalizeSearchTerm(term) {
	return typeof term === 'string' ? term.trim().toLowerCase() : '';
}

function ConsolidatedPartCard({ part }) {
	const isBloomfireCard =
		typeof part?.imageurl === 'string' &&
		typeof part?.obsolete === 'string' &&
		BLOOMFIRE_SENTINELS.has(part.imageurl.toLowerCase()) &&
		BLOOMFIRE_SENTINELS.has(part.obsolete.toLowerCase());

	if (isBloomfireCard) {
		return (
			<div className='relative flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900'>
				<SearchResultPartsBloomfireBusinessLogicCard
					tableRowNum={part?.stockcode || part?.mfgstockcode || 'bloomfire-card'}
					url_or_note={part?.stockcode}
					backup_url_or_note={part?.mfgstockcode}
					businessLogicType={part?.imageurl}
				/>
			</div>
		);
	}

	return <StandardConsolidatedPartCard part={part} />;
}

function StandardConsolidatedPartCard({ part }) {
	const {
		feedbackPanelStatesAll,
		handleFeedbackPanelStatesAll,
	} = useContext(AppContext);
	const [copied, setCopied] = React.useState(false);
	const sources = normalizeSources(part);
	const partTitle = [part?.mfgname?.trim(), part?.optimizeddescription || part?.description]
		.filter(Boolean)
		.join(' ')
		.trim();

	const partImage = part?.imageurl
		? part.imageurl.includes('?thumb')
			? part.imageurl
			: `${part.imageurl}?thumb`
		: FALLBACK_IMAGE;
	const isObsolete = part?.obsolete && part.obsolete !== 'N';
	const hasReplacement = part?.replacedby && part.replacedby.trim() !== '';
	const replacementText = hasReplacement ? `Replaced by ${part.replacedby}` : 'No replacement';

	// Construct PartsTown link
	const partLink = part?.mfgname && part?.stockcode
		? `https://www.partstown.com/${part.mfgname.toLowerCase()}/${
			(part?.replacedby && part.replacedby.trim() !== '')
				? part.replacedby.toLowerCase()
				: part.stockcode.toLowerCase()
		}`
		: null;

	// Construct replacement part link
	const replacementPartLink = part?.mfgname && hasReplacement && part.replacedby !== 'NOT IN WEBSITE'
		? `https://www.partstown.com/${part.mfgname.toLowerCase()}/${part.replacedby.toLowerCase()}`
		: null;

	const handleCopyPartsTown = () => {
		if (part?.stockcode) {
			navigator.clipboard.writeText(part.stockcode);
			setCopied(true);
			setTimeout(() => {
				setCopied(false);
			}, 2000);
		}
	};

	return (
		<div className='relative flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900'>
			<div className='flex flex-col gap-4 p-4 sm:flex-row sm:p-6'>
				<div className='mx-auto flex w-full max-w-[281px] shrink-0 items-start justify-center sm:mx-0'>
					{partLink ? (
						<a href={partLink} target='_blank' rel='noopener noreferrer' className='cursor-pointer'>
							<img
								src={partImage}
								alt={partTitle || part?.stockcode || 'Part image'}
								className='h-[270px] w-[281px] rounded-lg border border-zinc-100 object-contain shadow-sm dark:border-zinc-700 hover:opacity-90 transition-opacity'
							/>
						</a>
					) : (
						<img
							src={partImage}
							alt={partTitle || part?.stockcode || 'Part image'}
							className='h-[270px] w-[281px] rounded-lg border border-zinc-100 object-contain shadow-sm dark:border-zinc-700'
						/>
					)}
				</div>

				<div className='flex flex-1 flex-col gap-3 pb-20'>
					<div className='flex flex-col gap-2'>
						<div className='flex flex-col gap-1'>
							<div className='flex flex-row items-center gap-2'>
								{partLink ? (
									<a href={partLink} target='_blank' rel='noopener noreferrer' className='no-underline hover:decoration-blue-600 dark:hover:decoration-blue-400 hover:underline hover:decoration-2'>
										<h3 className='text-lg font-semibold text-zinc-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors'>
											{partTitle || part?.stockcode || 'Part'}
										</h3>
									</a>
								) : (
									<h3 className='text-lg font-semibold text-zinc-900 dark:text-white'>
										{partTitle || part?.stockcode || 'Part'}
									</h3>
								)}
								{isObsolete && (
									<div className="h-6 px-3.5 bg-amber-700 rounded-[30px] inline-flex flex-col justify-center items-center">
										<div className="justify-center text-white text-xs font-bold font-sf-pro uppercase leading-none tracking-wide">Obsolete</div>
									</div>
								)}
							</div>
							<div className='text-sm text-zinc-600 dark:text-zinc-300'>
								<div className='flex items-center gap-2'>
									<span>Parts Town #: {part?.stockcode || 'N/A'}</span>
									{part?.stockcode && (
										<button
											onClick={handleCopyPartsTown}
											className='inline-flex items-center justify-center transition-opacity hover:opacity-70'
											aria-label={copied ? 'Copied!' : 'Copy Parts Town number to clipboard'}
											type='button'
										>
											<img src={copied ? CheckIcon : CopyIcon} alt='' className='h-4 w-4' />
										</button>
									)}
								</div>
								<div>Manufacturer #: {part?.mfgstockcode || 'N/A'}</div>
							</div>
							{isObsolete ? (
								<div className='text-xs font-semibold uppercase tracking-wide text-orange-600'>
									{hasReplacement ? (
										<>
											Replaced by{' '}
											{replacementPartLink ? (
												<a
													href={replacementPartLink}
													target='_blank'
													rel='noopener noreferrer'
													className='text-orange-600 underline hover:text-blue-600 dark:text-orange-400 dark:hover:text-blue-400'
												>
													{part.replacedby}
												</a>
											) : (
												<span>{part.replacedby}</span>
											)}
										</>
									) : (
										'No replacement'
									)}
								</div>
							) : (
								part?.replacedby && (
									<div className='text-xs font-semibold uppercase tracking-wide text-lime-600'>
										Replacement available — Replaced by{' '}
										{replacementPartLink ? (
											<a
												href={replacementPartLink}
												target='_blank'
												rel='noopener noreferrer'
												className='text-lime-600 underline hover:text-blue-600 dark:text-lime-400 dark:hover:text-blue-400'
											>
												{part.replacedby}
											</a>
										) : (
											<span>{part.replacedby}</span>
										)}
									</div>
								)
							)}
						</div>
					</div>

					<div className='flex flex-wrap items-center gap-2 text-sm'>
						<span className='font-medium text-zinc-700 dark:text-zinc-300'>Search Term:</span>
						{part?.search_term ? (
							<span className='rounded-md border border-zinc-200 px-2 py-1 text-xs uppercase tracking-wide text-zinc-600 dark:border-zinc-700 dark:text-zinc-300'>
								{typeof part.search_term === 'string' ? part.search_term : JSON.stringify(part.search_term)}
							</span>
						) : (
							<span className='text-xs text-zinc-500'>Not captured</span>
						)}
					</div>

					<div className='flex flex-wrap gap-2'>
						<span className='text-sm font-medium text-zinc-700 dark:text-zinc-300'>Sources:</span>
						{Object.entries(SOURCE_BADGE_META)
							.filter(([sourceId]) => sources.includes(sourceId))
							.map(([sourceId, meta]) => (
								<Badge key={sourceId} color={meta.color}>
									{meta.label}
								</Badge>
							))}
					</div>

					{part?.notes && typeof part.notes === 'string' && part.notes.trim() !== '' && (
						<div className='rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-200'>
							{part.notes}
						</div>
					)}
				</div>
			</div>

		<div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6">
			<FeedbackPanel
				typeOfResult={(part?.data_sources && part.data_sources[0]) || 'consolidated'}
				optPartDesc={part?.optimizeddescription || ''}
				mfgName={part?.mfgname || ''}
				repPartName={part?.replacedby || ''}
				ptPartName={part?.stockcode || ''}
				mfgPartName={part?.mfgstockcode || ''}
				partDesc={part?.description || ''}
				imgURL={part?.imageurl || ''}
				partObsolete={part?.obsolete || ''}
				notes={part?.notes || ''}
				feedbackPanelStates={feedbackPanelStatesAll}
				handleFeedbackPanelStates={handleFeedbackPanelStatesAll}
			/>
		</div>
	</div>
	);
}

function EmptyState({ showCrossValidationHint = false }) {
	return (
		<div className='flex flex-col items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-white p-12 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400'>
			<p>No consolidated results yet.</p>
			{showCrossValidationHint && (
				<p className='mt-2 text-xs text-zinc-400'>
					Run the existing split view to cross-check data while the new API soaks.
				</p>
			)}
		</div>
	);
}

function SearchResultsNew() {
	const {
		searchResultsPartsAll,
		searchResultsPartsAllTraceback,
		isLoading,
		isConsolidatedLoading,
		searchResultsPartsRC,
		searchResultsPartsSNL,
		searchResultsPartsWebsite,
		searchResultsPartsParsed,
		activeFilter,
		activeSearchTermFilter,
	} = useContext(AppContext);

	const hasAnySplitResults =
		(Boolean(searchResultsPartsRC?.parts) && Object.keys(searchResultsPartsRC.parts).length > 0) ||
		(Boolean(searchResultsPartsSNL) && Object.keys(searchResultsPartsSNL).length > 0) ||
		(Boolean(searchResultsPartsWebsite) && Object.keys(searchResultsPartsWebsite).length > 0) ||
		(Boolean(searchResultsPartsParsed) && Object.keys(searchResultsPartsParsed).length > 0);

	// Merge parts from the four data sources in order: SNL -> Website -> RC -> Parsed
	const mergedParts = useMemo(() => {
		const seen = new Map();

		// Helper function to add parts from a data source
		const addPartsFromSource = (partsData, sourceId) => {
			if (!partsData || typeof partsData !== 'object') {
				return;
			}

			// Handle different data structures
			let partsArray = [];

			// For RC data which has a different structure
			if (sourceId === 'rc' && partsData.parts) {
				// partsData.parts is an object with model names as keys
				Object.values(partsData.parts).forEach(modelParts => {
					if (Array.isArray(modelParts)) {
						partsArray = partsArray.concat(modelParts);
					}
				});
			} else {
				// For SNL, Website, and Parsed which have model names as keys
				Object.values(partsData).forEach(modelParts => {
					if (Array.isArray(modelParts)) {
						partsArray = partsArray.concat(modelParts);
					}
				});
			}

			// Process each part
			partsArray.forEach((part) => {
				if (!partHasRenderableContent(part)) {
					return;
				}

				const key = (part?.mfgstockcode || part?.stockcode || '').toLowerCase();
				if (!key) {
					return;
				}

				if (!seen.has(key)) {
					// New part - add it with the source
					const newPart = {
						...part,
						data_sources: [sourceId],
					};
					seen.set(key, newPart);
				} else {
					// Part already exists - merge data sources
					const existing = seen.get(key);
					const currentSources = new Set(normalizeSources(existing));
					currentSources.add(sourceId);
					existing.data_sources = Array.from(currentSources);

					// Merge other fields if they're missing
					if (!existing.imageurl && part.imageurl) {
						existing.imageurl = part.imageurl;
					}
					if ((!existing.optimizeddescription || existing.optimizeddescription === existing.description) && part.optimizeddescription) {
						existing.optimizeddescription = part.optimizeddescription;
					}
					if (!existing.description && part.description) {
						existing.description = part.description;
					}
					if (!existing.notes && part.notes) {
						existing.notes = part.notes;
					}
				}
			});
		};

		// Add parts in order: SNL -> Website -> RC -> Parsed
		addPartsFromSource(searchResultsPartsSNL, 'snl');
		addPartsFromSource(searchResultsPartsWebsite, 'web');
		addPartsFromSource(searchResultsPartsRC, 'rc');
		addPartsFromSource(searchResultsPartsParsed, 'parsed');

		return Array.from(seen.values());
	}, [searchResultsPartsSNL, searchResultsPartsWebsite, searchResultsPartsRC, searchResultsPartsParsed]);

	// Extract Bloomfire part from RC data (matches Improved view structure)
	const bloomfirePart =
		searchResultsPartsRC?.bf_links &&
		partHasRenderableContent({
			...searchResultsPartsRC.bf_links,
			imageurl: searchResultsPartsRC.bf_links.imageurl || 'BLOOMFIRE',
			obsolete: searchResultsPartsRC.bf_links.obsolete || 'BLOOMFIRE',
		})
			? {
					...searchResultsPartsRC.bf_links,
					imageurl: searchResultsPartsRC.bf_links.imageurl || 'BLOOMFIRE',
					obsolete: searchResultsPartsRC.bf_links.obsolete || 'BLOOMFIRE',
			  }
			: null;

	const totalPartsCount = mergedParts.length + (bloomfirePart ? 1 : 0);

	const normalizedActiveSearchTerm = useMemo(() => {
		if (!activeSearchTermFilter || activeSearchTermFilter === 'all') {
			return '';
		}
		return normalizeSearchTerm(activeSearchTermFilter);
	}, [activeSearchTermFilter]);

	const filteredParts = useMemo(() => {
		const filteredBySource = activeFilter === 'all'
			? mergedParts
			: mergedParts.filter((part) => normalizeSources(part).includes(activeFilter));

		if (!normalizedActiveSearchTerm) {
			return filteredBySource;
		}

		return filteredBySource.filter((part) => {
			const term = part?.search_term ?? part?.searchTerm ?? '';
			return normalizeSearchTerm(term) === normalizedActiveSearchTerm;
		});
	}, [mergedParts, activeFilter, normalizedActiveSearchTerm]);

	const showEmptyState = !isConsolidatedLoading && totalPartsCount === 0;

	// Error handling (matches Improved view structure, though New view doesn't use consolidated endpoint)
	const hasConsolidatedError = false; // New view doesn't use consolidated endpoint
	const consolidatedErrorMessage = '';

	const shouldShowBloomfirePart = useMemo(() => {
		if (!bloomfirePart) {
			return false;
		}
		if (!normalizedActiveSearchTerm) {
			return true;
		}
		const term = bloomfirePart?.search_term ?? bloomfirePart?.searchTerm ?? '';
		return normalizeSearchTerm(term) === normalizedActiveSearchTerm;
	}, [bloomfirePart, normalizedActiveSearchTerm]);

	return (
		<div className='flex flex-col gap-4'>
			{hasConsolidatedError && (
				<div className='rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200'>
					<p className='font-semibold'>Consolidated results incomplete</p>
					<p className='mt-1 text-xs'>
						{typeof consolidatedErrorMessage === 'string'
							? consolidatedErrorMessage
							: 'The consolidated parts endpoint returned an error. Compare with the split view to validate coverage.'}
					</p>
				</div>
			)}

			{isConsolidatedLoading && totalPartsCount === 0 ? (
				<div className='flex flex-col gap-3 rounded-lg border border-dashed border-zinc-300 bg-white p-10 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400'>
					<p>Loading consolidated results…</p>
				</div>
			) : showEmptyState ? (
				<EmptyState showCrossValidationHint={hasAnySplitResults} />
			) : (
				<div className='flex flex-col gap-4'>
					{filteredParts.length === 0 && (activeFilter !== 'all' || normalizedActiveSearchTerm) ? (
						<div className='rounded-lg border border-dashed border-zinc-200 bg-white p-6 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400'>
							No results match the selected filter.
						</div>
					) : (
						<>
						{shouldShowBloomfirePart && (
							<ConsolidatedPartCard
								key={`${bloomfirePart?.mfgstockcode || bloomfirePart?.stockcode || 'bloomfire'}-top`}
								part={bloomfirePart}
							/>
						)}
						{filteredParts.map((part, index) => (
							<ConsolidatedPartCard
								key={`${part?.mfgstockcode || part?.stockcode || 'part'}-${index}`}
								part={part}
							/>
						))}
						</>
					)}
				</div>
			)}
		</div>
	);
}

export default SearchResultsNew;
